import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import {
  BarChart,
  List,
  Wind,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Sun,
  Info,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../../ui/shadcn/tabs";
import { Alert } from "../../ui/shadcn/alert";
import HighchartsReact from "highcharts-react-official";
// ATLAS-581: gráfico "Radiação Solar Global Horizontal" removido do modal de estatísticas
// import { SolarChart } from "./SolarChart";
import { DistributionChart } from "../DistributionChart";
import { DistanceInfo } from "../DistanceInfo";
import LegendTabComponent from "../../TabLegendComponent";
// ATLAS-581: gráfico "Radiação Solar Global Horizontal" removido do modal de estatísticas
// import { useLayers } from "../../../context/LayersContext";
import {
  AreaStats,
  DistributionBin,
  DistanceToLayerResponse,
  StatCard,
  ApiGetStateStats,
  WindSeriesResponse,
  WindDensityResponse,
  WindDistributionResponse,
  WindPotentialResponse,
  WindCumulativeResponse,
  WindScenario,
  GenerationPowerSummaryResponse,
  ProjectionAreaStatsResponse,
} from "../../../services/api";

import { WindChart } from "../WindChart/WindChart";
import { domToPng } from "modern-screenshot";
import { captureChart } from "../../ReportComponent/utils/captureComponents";
import { PowerDensityChart } from "../PowerDensityChart";
import { WindDistributionChart } from "../WindDistributionChart";
import { WindPotentialChart } from "../WindPotentialChart";
import { WindCumulativeChart } from "../WindCumulativeChart";
import WindRoseChart from "../../charts/wind-rose/WindRoseChart";
import { WindRose } from "@/types/wind";
import {
  PROJECTION_LAYER_OPTIONS,
  EOLICO_PROJECTION_LAYER_OPTIONS,
} from "../../../global/constants/projectionLayers";
import { getAreaNotAdequateWarning, hasZeroStats } from "../utils/areaStats";
import { RestrictionBreakdown } from "../RestrictionBreakdown";
import type { RestrictionEntry } from "../../../services/api";
import { useRestrictionBreakdown } from "../RestrictionBreakdown/useRestrictionBreakdown";

const WIND_SCENARIO_OPTIONS = [
  { label: "Teórico", scenario: WindScenario.Teorico },
  { label: "Geográfico", scenario: WindScenario.Geografico },
] as const;

const WIND_VIEW_COUNT = 7;
const WIND_DENSITY_DISTRIBUTION_VIEW_INDEX = 6;

enum EnergyMode {
  Solar = "solar",
  Eolico = "eolico",
}

export type StatisticsPanelTab = "statistics" | "legends";

interface StatisticsPanelProps {
  onClose: () => void;
  latitude: string | null;
  longitude: string | null;
  address: string | null;
  globalIrradiation: string | null;
  difuseIrradiation: string | null;
  directIrradiation: string | null;
  monthlyIrradiation: number[];
  annualMean?: number;
  distributionData?: DistributionBin[];
  areaStats?: AreaStats | null;
  distanceData?: {
    existing?: DistanceToLayerResponse;
    planned?: DistanceToLayerResponse;
    substation_existing?: DistanceToLayerResponse;
    substation_planned?: DistanceToLayerResponse;
  } | null;
  selectedDrawing?: { id: string; name: string; type: string; result?: string | null } | null;
  selectedCityName?: string | null;
  /** True whenever an area/city/imported layer is selected, regardless of whether its
   * potencial/distância have been calculated yet — drives the two "Calcular…" buttons,
   * which must show as soon as something is selected, not only once data has loaded. */
  hasSelection?: boolean;
  /** Área (km²) da geometria selecionada, medida no cliente — mostra a "Área Total"
   * de cidades/camadas importadas antes de "Calcular potencial" ser pressionado */
  selectionAreaKm2?: number | null;
  /** Áreas desenhadas no mapa disponíveis para seleção via dropdown (Dados Técnicos) */
  areaDrawings?: Array<{ id: string; name: string }>;
  /** Seleciona (ou limpa, com null) uma área desenhada — mesmo efeito de clicar nela no mapa */
  onSelectAreaDrawing?: (id: string | null) => void;
  areaPotential?: StatCard[] | null;
  areaWindPotential?: StatCard[] | null;
  /** Currently selected wind measurement height (e.g. "100m") */
  windHeight: string;
  /** Callback to sync height selection across all wind charts */
  onWindHeightChange: (height: string) => void;
  /** Currently selected viability scenario for potential/cumulative charts */
  windScenario: WindScenario;
  /** Callback to sync scenario selection across potential and cumulative charts */
  onWindScenarioChange: (scenario: WindScenario) => void;
  /** Monthly wind velocity series data from GET /api/v1/report/wind-series */
  windSeriesData: WindSeriesResponse | null;
  /** Monthly wind power density data from GET /api/v1/report/wind-density */
  windDensityData: WindDensityResponse | null;
  /** Wind speed distribution histogram from POST /api/v1/report/wind-distribution */
  windDistributionData: WindDistributionResponse | null;
  /** Wind power potential per speed bin from POST /api/v1/report/wind-potential */
  windPotentialData: WindPotentialResponse | null;
  /** Cumulative wind power potential from POST /api/v1/report/wind-cumulative */
  windCumulativeData: WindCumulativeResponse | null;
  windRoseData: WindRose | null;
  /** Quando true (camada GHI), oculta o gráfico de série mensal e exibe só o histograma */
  isStaticSolarLayer?: boolean;
  /** Distribution histogram for wind power density (W/m²) */
  windDensityDistributionData?: DistributionBin[];
  /** Area stats for wind power density distribution */
  windDensityAreaStats?: AreaStats | null;
  systemMode: "sin" | "distribuidora";
  onSystemModeChange: (mode: "sin" | "distribuidora") => void;
  isLoadingDistance?: boolean;
  generationPowerSummary?: GenerationPowerSummaryResponse | null;
  selectedProjectionLayer?: string | null;
  onProjectionChange?: (layerName: string | null) => void;
  projectionAreaStats?: ProjectionAreaStatsResponse | null;
  isLoadingProjection?: boolean;
  isLoadingWindPotential?: boolean;
  activeTab?: StatisticsPanelTab;
  onActiveTabChange?: (tab: StatisticsPanelTab) => void;
  /** Calcula Área Propícia / Potencial Instalável / Energia Anual sob demanda */
  onCalculatePotential?: () => void;
  isLoadingPotential?: boolean;
  /** Calcula a distância até linhas de transmissão / subestações sob demanda */
  onCalculateDistance?: () => void;
  /** Restrição apontada no memorial de cálculo — o mapa destaca a área dela. */
  onHoverRestriction?: (entry: RestrictionEntry | null) => void;
  /** Geometria da área selecionada — o Memorial de Cálculo só existe com uma área */
  selectedGeometry?: GeoJSON.Geometry | null;
}

export interface StatisticsPanelRef {
  captureStatisticsImage: (
    energyType?: "solar" | "eolico"
  ) => Promise<Array<{ title: string; images: string[] }>>;
  getStatisticsData: () => {
    monthlyIrradiation: number[];
    annualMean?: number;
    statsCards: Array<{ label: string; value: string }>;
    solarStatsCards: Array<{ label: string; value: string }>;
    windStatsCards: Array<{ label: string; value: string }>;
    distributionData?: DistributionBin[];
    areaStats?: AreaStats | null;
    distanceData?: {
      existing?: DistanceToLayerResponse;
      planned?: DistanceToLayerResponse;
      substation_existing?: DistanceToLayerResponse;
      substation_planned?: DistanceToLayerResponse;
    } | null;
    drawingName?: string;
    /** Identifica a área cujos stats estão carregados (drawing.id ou `city-${nome}`) — usada para detectar quando o relatório pede uma marcação diferente da área ativa no painel */
    areaSourceId?: string;
    generationPowerSummary?: GenerationPowerSummaryResponse | null;
  };
}

type ButtonProps = {
  onClick: () => void;
  active: boolean;
  children: React.ReactNode;
  position?: "left" | "right";
};

const Button = ({
  onClick,
  active,
  children,
  position = "left",
}: ButtonProps) => (
  <button
    onClick={onClick}
    className={[
      "flex flex-1 items-center justify-center gap-2 px-4 py-3.5 text-sm border-none cursor-pointer transition-all duration-150 bg-white",
      position === "left" ? "rounded-l-xl" : "rounded-r-xl",
      active
        ? "font-semibold text-[#0C2856] border-b-2 border-[#0C2856]"
        : "font-normal text-gray-400 border-b-2 border-transparent",
    ].join(" ")}
  >
    {children}
  </button>
);

const WIND_ROSE_VARIABLES_SUMMARY = [
  { variableName: "< 4m/s", color: "#A7F3D0" },
  { variableName: "4-7m/s", color: "#34D399" },
  { variableName: "> 7m/s", color: "#227093" },
];

export const StatisticsPanel = forwardRef<
  StatisticsPanelRef,
  StatisticsPanelProps
>(
  (
    {
      onClose,
      monthlyIrradiation,
      annualMean,
      distributionData,
      areaStats,
      distanceData,
      selectedDrawing,
      selectedCityName,
      hasSelection = false,
      selectionAreaKm2 = null,
      areaDrawings,
      onSelectAreaDrawing,
      areaPotential,
      areaWindPotential,
      windHeight,
      onWindHeightChange,
      windScenario,
      onWindScenarioChange,
      windSeriesData,
      windDensityData,
      windDistributionData,
      windPotentialData,
      windCumulativeData,
      windRoseData,
      isStaticSolarLayer = false,
      windDensityDistributionData,
      windDensityAreaStats,
      systemMode,
      onSystemModeChange,
      isLoadingDistance = false,
      generationPowerSummary,
      selectedProjectionLayer,
      onProjectionChange,
      projectionAreaStats,
      selectedGeometry,
      isLoadingProjection = false,
      isLoadingWindPotential = false,
      activeTab,
      onActiveTabChange,
      onCalculatePotential,
      isLoadingPotential = false,
      onCalculateDistance,
      onHoverRestriction,
    }: StatisticsPanelProps,
    ref
  ) => {
    // ATLAS-581: gráfico "Radiação Solar Global Horizontal" removido do modal de estatísticas
    // const { layerTimeValues } = useLayers();

    const [internalMainTab, setInternalMainTab] =
      useState<StatisticsPanelTab>("statistics");
    const [energyMode, setEnergyMode] = useState<EnergyMode>(EnergyMode.Solar);
    // ATLAS-581: gráfico "Radiação Solar Global Horizontal" removido do modal de estatísticas
    // const [viewIndex, setViewIndex] = useState(0);
    const [windViewIndex, setWindViewIndex] = useState(0);
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
    const [projectionDropdownOpen, setProjectionDropdownOpen] = useState(false);
    const [windScenarioDropdownOpen, setWindScenarioDropdownOpen] = useState(false);
    const [eolicoProjectionDropdownOpen, setEolicoProjectionDropdownOpen] = useState(false);
    const [areaDropdownOpen, setAreaDropdownOpen] = useState(false);
    const toggleTriggerRef = useRef<HTMLDivElement>(null);

    // ATLAS-581: gráfico "Radiação Solar Global Horizontal" removido do modal de estatísticas
    // const solarChartRef = useRef<HighchartsReact.RefObject>(null);
    const windChartRef = useRef<HighchartsReact.RefObject>(null);
    const powerDensityChartRef = useRef<HighchartsReact.RefObject>(null);
    const statsCardsSectionRef = useRef<HTMLDivElement>(null);
    const mainTab = activeTab ?? internalMainTab;
    const setMainTab = useCallback(
      (tab: StatisticsPanelTab) => {
        setInternalMainTab(tab);
        onActiveTabChange?.(tab);
      },
      [onActiveTabChange]
    );

    useImperativeHandle(ref, () => ({
      captureStatisticsImage: async (energyType?: "solar" | "eolico") => {
        const sections: Array<{ title: string; images: string[] }> = [];
        const savedMainTab = mainTab;
        const savedEnergyMode = energyMode;
        // ATLAS-581: gráfico "Radiação Solar Global Horizontal" removido do modal de estatísticas
        // const savedViewIndex = viewIndex;
        const savedWindViewIndex = windViewIndex;
        const captureSolar = !energyType || energyType === "solar";
        const captureWind = !energyType || energyType === "eolico";

        try {
          if (captureSolar) {
            setMainTab("statistics");
            setEnergyMode(EnergyMode.Solar);
            // ATLAS-581: gráfico "Radiação Solar Global Horizontal" removido do modal de estatísticas
            // setViewIndex(0);
            await new Promise((resolve) => setTimeout(resolve, 300));
            const solarImages: string[] = [];
            if (statsCardsSectionRef.current) {
              try {
                const img = await domToPng(statsCardsSectionRef.current, {
                  scale: 2,
                  backgroundColor: "#ffffff",
                });
                if (img) solarImages.push(img);
              } catch {
                /* best-effort */
              }
            }
            // ATLAS-581: gráfico "Radiação Solar Global Horizontal" removido do modal de estatísticas
            // const chartImg = await captureChart(
            //   solarChartRef.current,
            //   "Radiação Solar Global Horizontal"
            // );
            // if (chartImg) solarImages.push(chartImg);
            if (solarImages.length > 0)
              sections.push({ title: "Energia Solar", images: solarImages });
          }
          if (captureWind) {
            setEnergyMode(EnergyMode.Eolico);
            await new Promise((resolve) => setTimeout(resolve, 300));
            const windImages: string[] = [];
            if (statsCardsSectionRef.current) {
              try {
                const img = await domToPng(statsCardsSectionRef.current, {
                  scale: 2,
                  backgroundColor: "#ffffff",
                });
                if (img) windImages.push(img);
              } catch {
                /* best-effort */
              }
            }
            const chartImg = await captureChart(
              windChartRef.current,
              "Velocidade do Vento Mensal"
            );
            if (chartImg) windImages.push(chartImg);
            if (windImages.length > 0)
              sections.push({ title: "Energia Eólica", images: windImages });
          }
        } finally {
          setMainTab(savedMainTab);
          setEnergyMode(savedEnergyMode);
          // ATLAS-581: gráfico "Radiação Solar Global Horizontal" removido do modal de estatísticas
          // setViewIndex(savedViewIndex);
          setWindViewIndex(savedWindViewIndex);
        }
        return sections;
      },
      getStatisticsData: () => ({
        monthlyIrradiation: cachedMonthlyData,
        annualMean,
        statsCards: STATS,
        solarStatsCards: hasAreaData
          ? areaPotential ?? _emptyStats
          : stateSolarStats,
        windStatsCards: hasAreaData
          ? areaWindPotential ?? _emptyStats
          : stateWindStats,
        distributionData,
        areaStats,
        distanceData,
        drawingName: selectedDrawing?.name,
        areaSourceId: hasAreaData
          ? selectedDrawing?.id ??
            (selectedCityName ? `city-${selectedCityName}` : undefined)
          : undefined,
        generationPowerSummary,
        windSeriesData,
        windDensityData,
        windDistributionData,
        windPotentialData,
        windCumulativeData,
        windRoseData,
      }),
    }));

    const [cachedMonthlyData, setCachedMonthlyData] = useState<number[]>(
      new Array(12).fill(0)
    );
    const _emptyStats: StatCard[] = [
      { label: "Área Total", value: "—" },
      { label: "Área Propícia", value: "—" },
      { label: "Potencial Instalável", value: "—" },
      { label: "Energia Anual", value: "—" },
    ];
    const [stateSolarStats, setStateSolarStats] =
      useState<StatCard[]>(_emptyStats);
    const [stateWindStats, setStateWindStats] =
      useState<StatCard[]>(_emptyStats);

    useEffect(() => {
      ApiGetStateStats("solar")
        .then((res) => setStateSolarStats(res.stats))
        .catch(() => {});
      ApiGetStateStats("wind")
        .then((res) => setStateWindStats(res.stats))
        .catch(() => {});
    }, []);

    useEffect(() => {
      const hasRealData =
        monthlyIrradiation && monthlyIrradiation.some((val) => val > 0);
      if (hasRealData) setCachedMonthlyData(monthlyIrradiation);
    }, [monthlyIrradiation]);

    /* ATLAS-581: gráfico "Radiação Solar Global Horizontal" removido do modal de estatísticas
    const currentTimeString = layerTimeValues["pvout"];
    const activeIndexRef = useRef(0);
    let activeIndex = activeIndexRef.current;
    if (currentTimeString) {
      const parts = currentTimeString.split("-");
      if (parts.length >= 2) {
        const index = parseInt(parts[1], 10) - 1;
        if (!isNaN(index) && index >= 0 && index <= 11) {
          activeIndexRef.current = index;
          activeIndex = index;
        }
      }
    }
    */

    // ATLAS-581: gráfico "Radiação Solar Global Horizontal" removido do modal de estatísticas
    // const toggleView = () => setViewIndex((prev) => (prev === 0 ? 1 : 0));
    const prevWindView = () =>
      setWindViewIndex((prev) => (prev - 1 + WIND_VIEW_COUNT) % WIND_VIEW_COUNT);
    const nextWindView = () =>
      setWindViewIndex((prev) => (prev + 1) % WIND_VIEW_COUNT);

    const hasAreaData = areaPotential != null || areaWindPotential != null;

    let STATS: StatCard[] = _emptyStats;
    if (hasAreaData) {
      STATS =
        energyMode === EnergyMode.Solar
          ? areaPotential ?? _emptyStats
          : areaWindPotential ?? _emptyStats;
    } else {
      STATS =
        energyMode === EnergyMode.Solar ? stateSolarStats : stateWindStats;
    }

    const getWindViewLabel = (): string => {
      switch (windViewIndex) {
        case 0:
          return "Velocidade do Vento Mensal";
        case 1:
          return "Densidade de Potência Eólica Mensal";
        case 2:
          return "Distribuição de Velocidade (Área)";
        case 3:
          return "Potencial Eólico";
        case 4:
          return "Potencial Eólico Cumulativo";
        case 5:
          return "Velocidade e Direção do Vento";
        case WIND_DENSITY_DISTRIBUTION_VIEW_INDEX:
          return "Distribuição de Densidade de Potência Eólica";
        default:
          return "Velocidade do Vento Mensal";
      }
    };

    const renderWindChart = useCallback(() => {
      switch (windViewIndex) {
        case 0:
          return (
            <WindChart
              ref={windChartRef}
              data={windSeriesData}
              selectedHeight={windHeight}
              onHeightChange={onWindHeightChange}
            />
          );
        case 1:
          return (
            <PowerDensityChart
              ref={powerDensityChartRef}
              data={windDensityData}
              selectedHeight={windHeight}
              onHeightChange={onWindHeightChange}
            />
          );
        case 2:
          return (
            <WindDistributionChart
              distribution={windDistributionData?.distribution ?? []}
              stats={windDistributionData?.stats ?? null}
              selectedHeight={windHeight}
              onHeightChange={onWindHeightChange}
            />
          );
        case 3:
          return (
            <WindPotentialChart
              data={windPotentialData}
              selectedHeight={windHeight}
              onHeightChange={onWindHeightChange}
              selectedScenario={windScenario}
              onScenarioChange={onWindScenarioChange}
            />
          );
        case 4:
          return (
            <WindCumulativeChart
              data={windCumulativeData}
              selectedHeight={windHeight}
              onHeightChange={onWindHeightChange}
              selectedScenario={windScenario}
              onScenarioChange={onWindScenarioChange}
            />
          );
        case 5:
          return (
            <WindRoseChart
              variablesSummary={WIND_ROSE_VARIABLES_SUMMARY}
              data={windRoseData?.sectors || []}
              selectedHeight={windHeight}
              onHeightChange={onWindHeightChange}
              selectedScenario={windScenario}
            />
          );
        case WIND_DENSITY_DISTRIBUTION_VIEW_INDEX:
          return (
            <DistributionChart
              distribution={windDensityDistributionData || []}
              stats={windDensityAreaStats || null}
              unit="W/m²"
              barColor="#34D399"
            />
          );
        default:
          return (
            <WindChart
              ref={windChartRef}
              data={windSeriesData}
              selectedHeight={windHeight}
              onHeightChange={onWindHeightChange}
            />
          );
      }
    }, [
      windViewIndex,
      windHeight,
      onWindHeightChange,
      windScenario,
      onWindScenarioChange,
      windSeriesData,
      windDensityData,
      windDistributionData,
      windPotentialData,
      windCumulativeData,
      windRoseData,
      windDensityDistributionData,
      windDensityAreaStats,
    ]);

    const isSolarEnergyMode = energyMode === EnergyMode.Solar;

    // A projection picked in one mode points at the wrong GeoServer layer for the
    // other. The mode toggle buttons clear the projection, but mode also changes
    // without them (report capture, static wind layer effect) — so treat a
    // cross-mode selection as "nothing selected" everywhere: label, stat cards
    // and the calculate button, which otherwise would send a solar layer into an
    // eólico calculation.
    const modeProjectionOptions: readonly { label: string; layerName: string }[] =
      isSolarEnergyMode ? PROJECTION_LAYER_OPTIONS : EOLICO_PROJECTION_LAYER_OPTIONS;
    const modeValidProjectionLayer =
      selectedProjectionLayer &&
      modeProjectionOptions.some((p) => p.layerName === selectedProjectionLayer)
        ? selectedProjectionLayer
        : null;

    // Memorial de Cálculo — carregado só quando o usuário pede, mas acompanha a
    // troca de nível enquanto estiver aberto. Passa modeValidProjectionLayer, não
    // selectedProjectionLayer, para não pedir o memorial de uma camada solar
    // enquanto o painel está em modo eólico.
    // A altura vai junto: os cards de área propícia são pedidos com hub_height_m
    // (useSelectedArea), então sem ela o memorial descreveria um cálculo eólico
    // diferente do que está na tela.
    const restrictionBreakdown = useRestrictionBreakdown({
      layerName: modeValidProjectionLayer,
      geometry: selectedGeometry ?? null,
      hubHeightM: parseInt(windHeight.replace(/\D/g, ""), 10) || undefined,
    });

    const _fmtBr = (value: number, decimals: number) =>
      value.toLocaleString("pt-BR", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

    const _fmtArea = (km2: number): string => {
      if (km2 >= 1_000_000) return `${_fmtBr(km2 / 1_000_000, 2)} Mkm²`;
      if (km2 >= 1_000)     return `${_fmtBr(km2 / 1_000, 2)} × 10³ km²`;
      if (km2 >= 1)         return `${_fmtBr(km2, 2)} km²`;
      // Below 1 km² drop to m² so small drawn areas don't round down to "0,00 km²"
      return `${_fmtBr(km2 * 1_000_000, 0)} m²`;
    };

    const _fmtPower = (gw: number): string => {
      if (gw >= 1_000) return `${_fmtBr(gw / 1_000, 2)} TW`;
      if (gw >= 1)     return `${_fmtBr(gw, 2)} GW`;
      const mw = gw * 1_000;
      if (mw >= 1) return `${_fmtBr(mw, 1)} MW`;
      const kw = mw * 1_000;
      if (kw >= 1) return `${_fmtBr(kw, 1)} kW`;
      return `${_fmtBr(kw * 1_000, 0)} W`;
    };

    const _fmtEnergy = (gwh: number): string => {
      if (gwh >= 1_000_000) return `${_fmtBr(gwh / 1_000_000, 2)} PWh`;
      if (gwh >= 1_000)     return `${_fmtBr(gwh / 1_000, 2)} TWh`;
      if (gwh >= 1)         return `${_fmtBr(gwh, 1)} GWh`;
      const mwh = gwh * 1_000;
      if (mwh >= 1) return `${_fmtBr(mwh, 1)} MWh`;
      const kwh = mwh * 1_000;
      if (kwh >= 1) return `${_fmtBr(kwh, 1)} kWh`;
      return `${_fmtBr(kwh * 1_000, 0)} Wh`;
    };

    const PROJECTION_DEPENDENT = new Set(["Área Propícia", "Potencial Instalável", "Energia Anual"]);

    const displayedStats: StatCard[] = STATS.map((card) => {
      // Reflect the drawing picked in the "Área:" dropdown immediately — its own
      // measurement (from the ruler tool) is available before "Calcular potencial"
      // is ever pressed, and gets replaced with the backend's own value once it is.
      if (card.label === "Área Total" && selectedDrawing?.result) {
        return { ...card, value: selectedDrawing.result };
      }
      // Cidades e camadas importadas não têm selectedDrawing — sem isto o card
      // manteria a área do Estado (STATS estadual) até o cálculo manual.
      if (
        card.label === "Área Total" &&
        hasSelection &&
        !hasAreaData &&
        selectionAreaKm2 != null
      ) {
        return { ...card, value: _fmtArea(selectionAreaKm2) };
      }
      if (!PROJECTION_DEPENDENT.has(card.label)) return card;
      // Without a selection the cards already carry the state-wide values loaded
      // from the API — keep them instead of replacing with an instruction.
      if (!hasSelection) return card;
      if (!modeValidProjectionLayer) return { ...card, value: "Selecione uma projeção" };
      if (!projectionAreaStats) return { ...card, value: "—" };
      if (card.label === "Área Propícia")
        return { ...card, value: _fmtArea(projectionAreaStats.propitious_area_km2) };
      if (card.label === "Potencial Instalável")
        return { ...card, value: _fmtPower(projectionAreaStats.installable_gw) };
      if (card.label === "Energia Anual")
        return { ...card, value: _fmtEnergy(projectionAreaStats.annual_energy_gwh) };
      return card;
    });

    // Avalia os cards EXIBIDOS (que refletem a projeção selecionada), não os
    // STATS legados — evita alertar/esconder gráficos com base em valores que
    // não estão na tela. Guarda contra loading para não exibir aviso obsoleto
    // enquanto um novo cálculo está em andamento.
    const isAreaInadequate =
      hasAreaData &&
      !isLoadingPotential &&
      !isLoadingProjection &&
      hasZeroStats(displayedStats);

    const energyLabel = isSolarEnergyMode ? "Solar" : "Eólico";
    let headingText = "";
    if (selectedDrawing) {
      // Show the selected area's name as soon as it's picked, same as city
      // selection below — don't wait on hasAreaData (the stats can still be loading).
      headingText = `Área: ${selectedDrawing.name}`;
    } else if (selectedCityName) {
      headingText = `Potencial ${energyLabel} de ${selectedCityName}`;
    } else {
      headingText = `Potencial ${energyLabel} do Estado`;
    }

    return (
      <>
        <style>{`
        @keyframes sway {
          0%, 100% { transform: rotate(-20deg); }
          50% { transform: rotate(20deg); }
        }
      `}</style>
        <div
          data-tour="statistics-panel"
          role="region"
          aria-label="Painel de estatísticas"
          className="fixed bg-white flex flex-col rounded-xl shadow-2xl border border-gray-100 w-[456px]"
          style={{
            top: "168px",
            right: "20px",
            bottom: "10.5rem",
            zIndex: 1003,
            overflow: "hidden",
          }}
        >
          <div className="shrink-0">
            <Tabs
              value={mainTab}
              onValueChange={(v) => setMainTab(v as StatisticsPanelTab)}
            >
              <TabsList className="grid grid-cols-2 w-full mt-0 bg-white border-b border-gray-100">
                <TabsTrigger
                  value="statistics"
                  icon={<BarChart size={18} />}
                  className="text-[12px] font-bold min-h-[48px] text-[#0C2856]"
                >
                  DADOS TÉCNICOS
                </TabsTrigger>
                <TabsTrigger
                  value="legends"
                  icon={<List size={18} />}
                  className="text-[12px] font-bold min-h-[48px] text-[#0C2856]"
                >
                  LEGENDAS
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div
            data-tour={
              mainTab === "statistics"
                ? "technical-data-panel"
                : "legends-panel"
            }
            role="region"
            aria-label={
              mainTab === "statistics"
                ? "Painel de dados técnicos"
                : "Painel de legendas"
            }
            className="overflow-y-auto flex-1 p-5 flex flex-col gap-4"
          >
            {mainTab === "statistics" && (
              <div className="flex flex-col gap-4">
                <h2 className="text-[15px] font-bold text-[#0C2856] m-0">
                  Dados Técnicos
                </h2>

                <div className="flex w-full rounded-xl border border-gray-200 bg-white">
                  <Button
                    onClick={() => { setEnergyMode(EnergyMode.Solar); onProjectionChange?.(null); }}
                    active={energyMode === EnergyMode.Solar}
                    position="left"
                  >
                    <Sun
                      size={20}
                      color="#636467"
                      className={
                        energyMode === EnergyMode.Solar && isLoadingDistance
                          ? "animate-spin"
                          : ""
                      }
                    />
                    Energia Solar
                  </Button>
                  <div className="w-px bg-gray-200 self-stretch" />
                  <Button
                    onClick={() => { setEnergyMode(EnergyMode.Eolico); onProjectionChange?.(null); }}
                    active={energyMode === EnergyMode.Eolico}
                    position="right"
                  >
                    <Wind
                      size={20}
                      color="#636467"
                      style={
                        energyMode === EnergyMode.Eolico && isLoadingDistance
                          ? { animation: "sway 1s ease-in-out infinite" }
                          : undefined
                      }
                    />
                    Energia Eólica
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-500 shrink-0">Área:</span>
                  <div className="relative">
                    <button
                      onClick={() =>
                        areaDrawings && areaDrawings.length > 0 &&
                        setAreaDropdownOpen((prev) => !prev)
                      }
                      disabled={!areaDrawings || areaDrawings.length === 0}
                      className={[
                        "flex items-center gap-1.5 px-3 py-1.5 bg-white text-[12px] font-medium transition-colors border rounded disabled:opacity-50 disabled:cursor-not-allowed",
                        !hasSelection && areaDrawings && areaDrawings.length > 0
                          ? "border-amber-400 ring-1 ring-amber-300 text-amber-600 hover:text-amber-700"
                          : "border-gray-200 text-[#64748B] hover:text-[#0C2856]",
                      ].join(" ")}
                    >
                      {selectedDrawing ? selectedDrawing.name : "Nenhuma (Estado)"}
                      <ChevronDown size={14} className={`transition-transform ${areaDropdownOpen ? "rotate-180" : ""}`} />
                    </button>
                    {areaDropdownOpen && areaDrawings && areaDrawings.length > 0 && (
                      <div className="absolute left-0 mt-1 w-[180px] max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                        <button
                          onClick={() => {
                            onSelectAreaDrawing?.(null);
                            setAreaDropdownOpen(false);
                          }}
                          className={`block w-full px-4 py-2 text-left text-[12px] transition-colors border-b border-gray-100
                            ${!selectedDrawing ? "bg-gray-50 text-[#0C2856] font-bold" : "text-gray-400 hover:bg-gray-50 hover:text-[#0C2856]"}`}
                        >
                          Nenhuma (Estado)
                        </button>
                        {areaDrawings.map((d) => (
                          <button
                            key={d.id}
                            onClick={() => {
                              onSelectAreaDrawing?.(d.id);
                              setAreaDropdownOpen(false);
                            }}
                            className={`block w-full px-4 py-2 text-left text-[12px] transition-colors
                              ${selectedDrawing?.id === d.id ? "bg-gray-50 text-[#0C2856] font-bold" : "text-gray-600 hover:bg-gray-50 hover:text-[#0C2856]"}`}
                          >
                            {d.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div ref={statsCardsSectionRef} className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-[14px] font-bold text-[#0C2856] m-0">
                      {headingText}
                    </h3>
                    <div
                      ref={toggleTriggerRef}
                      className="flex items-center gap-1 shrink-0"
                      onMouseEnter={() => {
                        const rect =
                          toggleTriggerRef.current?.getBoundingClientRect();
                        if (rect)
                          setTooltipPos({
                            top: rect.top - 8,
                            left: rect.right,
                          });
                        setTooltipVisible(true);
                      }}
                      onMouseLeave={() => setTooltipVisible(false)}
                    >
                      <span
                        className={`text-[11px] font-medium cursor-pointer select-none ${
                          systemMode === "sin"
                            ? "text-[#0C2856]"
                            : "text-gray-400"
                        }`}
                        onClick={() => onSystemModeChange("sin")}
                      >
                        SIN (Rede Básica)
                      </span>
                      <button
                        onClick={() =>
                          onSystemModeChange(
                            systemMode === "sin" ? "distribuidora" : "sin"
                          )
                        }
                        className="relative inline-flex items-center cursor-pointer border-none bg-transparent p-0 w-8 h-[18px]"
                        aria-label="Alternar sistema"
                      >
                        <span className="block rounded-full transition-colors duration-200 w-8 h-[18px] bg-[#0C2856]" />
                        <span
                          className="absolute block rounded-full bg-white shadow transition-transform duration-200 w-3.5 h-3.5 top-0.5 left-0.5"
                          style={{
                            transform:
                              systemMode === "distribuidora"
                                ? "translateX(14px)"
                                : "translateX(0)",
                          }}
                        />
                      </button>
                      <span
                        className={`text-[11px] font-medium cursor-pointer select-none ${
                          systemMode === "distribuidora"
                            ? "text-[#0C2856]"
                            : "text-gray-400"
                        }`}
                        onClick={() => onSystemModeChange("distribuidora")}
                      >
                        Distribuidora
                      </span>
                    </div>
                    {tooltipVisible &&
                      createPortal(
                        <div
                          style={{
                            position: "fixed",
                            top: tooltipPos.top,
                            left: tooltipPos.left,
                            transform: "translate(-100%, -100%)",
                            width: 256,
                            backgroundColor: "#1f2937",
                            color: "#fff",
                            fontSize: 11,
                            lineHeight: 1.5,
                            borderRadius: 6,
                            padding: "10px",
                            zIndex: 99999,
                            pointerEvents: "none",
                          }}
                        >
                          <strong>SIN (Rede Básica)</strong> – Sistema
                          Interligado Nacional: rede elétrica de alta tensão
                          que interliga as usinas geradoras e permite o
                          transporte de energia entre regiões do país.
                          <br />
                          <br />
                          <strong>Distribuidora</strong> – Sistema da
                          distribuidora local (Neoenergia): rede de média e
                          baixa tensão responsável por entregar energia ao
                          consumidor final.
                        </div>,
                        document.body
                      )}
                  </div>
                  {isSolarEnergyMode ? (
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-500 shrink-0">Área Propícia por:</span>
                      <div className="relative">
                        <button
                          onClick={() => setProjectionDropdownOpen(prev => !prev)}
                          className={[
                            "flex items-center gap-1.5 px-3 py-1.5 bg-white text-[12px] font-medium transition-colors border rounded",
                            hasSelection && !modeValidProjectionLayer
                              ? "border-amber-400 ring-1 ring-amber-300 text-amber-600 hover:text-amber-700"
                              : "border-gray-200 text-[#64748B] hover:text-[#0C2856]",
                          ].join(" ")}
                        >
                          {modeValidProjectionLayer
                            ? PROJECTION_LAYER_OPTIONS.find(p => p.layerName === modeValidProjectionLayer)?.label ?? "Selecionar..."
                            : "Selecionar..."}
                          <ChevronDown size={14} className={`transition-transform ${projectionDropdownOpen ? "rotate-180" : ""}`} />
                        </button>
                        {projectionDropdownOpen && (
                          <div className="absolute left-0 mt-1 w-[130px] bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                            {PROJECTION_LAYER_OPTIONS.map((p) => (
                              <button
                                key={p.layerName}
                                onClick={() => {
                                  onProjectionChange?.(p.layerName);
                                  setProjectionDropdownOpen(false);
                                }}
                                className={`block w-full px-4 py-2 text-left text-[12px] transition-colors
                                  ${selectedProjectionLayer === p.layerName ? "bg-gray-50 text-[#0C2856] font-bold" : "text-gray-600 hover:bg-gray-50 hover:text-[#0C2856]"}`}
                              >
                                {p.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                  {!isSolarEnergyMode && (
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-500 shrink-0">Área Propícia por:</span>
                      <div className="relative">
                        <button
                          onClick={() => setEolicoProjectionDropdownOpen(prev => !prev)}
                          className={[
                            "flex items-center gap-1.5 px-3 py-1.5 bg-white text-[12px] font-medium transition-colors border rounded",
                            hasSelection && !modeValidProjectionLayer
                              ? "border-amber-400 ring-1 ring-amber-300 text-amber-600 hover:text-amber-700"
                              : "border-gray-200 text-[#64748B] hover:text-[#0C2856]",
                          ].join(" ")}
                        >
                          {modeValidProjectionLayer
                            ? EOLICO_PROJECTION_LAYER_OPTIONS.find(p => p.layerName === modeValidProjectionLayer)?.label ?? "Selecionar..."
                            : "Selecionar..."}
                          <ChevronDown size={14} className={`transition-transform ${eolicoProjectionDropdownOpen ? "rotate-180" : ""}`} />
                        </button>
                        {eolicoProjectionDropdownOpen && (
                          <div className="absolute left-0 mt-1 w-[130px] bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                            {EOLICO_PROJECTION_LAYER_OPTIONS.map((p) => (
                              <button
                                key={p.layerName}
                                onClick={() => {
                                  onProjectionChange?.(p.layerName);
                                  setEolicoProjectionDropdownOpen(false);
                                }}
                                className={`block w-full px-4 py-2 text-left text-[12px] transition-colors
                                  ${selectedProjectionLayer === p.layerName ? "bg-gray-50 text-[#0C2856] font-bold" : "text-gray-600 hover:bg-gray-50 hover:text-[#0C2856]"}`}
                              >
                                {p.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={onCalculatePotential}
                    disabled={
                      !hasSelection ||
                      !modeValidProjectionLayer ||
                      isLoadingPotential ||
                      isLoadingProjection
                    }
                    title={
                      !hasSelection
                        ? "Selecione uma área para poder calcular"
                        : !modeValidProjectionLayer
                        ? "Escolha o tipo de área propícia para poder calcular"
                        : undefined
                    }
                    className={[
                      "flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-[12px] font-semibold border-none transition-colors shrink-0",
                      !hasSelection || !modeValidProjectionLayer || isLoadingPotential || isLoadingProjection
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-[#0C2856] text-white cursor-pointer hover:bg-[#0a2246]",
                    ].join(" ")}
                  >
                    {isLoadingPotential || isLoadingProjection
                      ? "Calculando…"
                      : "Calcular potencial"}
                  </button>
                  <div className="grid grid-cols-4 gap-2 shrink-0">
                    {displayedStats.map(({ label, value }, i) => {
                      const isCalculating =
                        hasSelection &&
                        (isLoadingPotential ||
                          (isLoadingProjection && PROJECTION_DEPENDENT.has(label)));
                      return (
                        <div
                          key={i}
                          className="flex flex-col gap-1.5 p-3 bg-gray-50 border border-gray-200 rounded-lg"
                        >
                          <span className="text-[11px] text-gray-500 leading-snug">
                            {label}
                          </span>
                          {isCalculating ? (
                            <div className="flex flex-col gap-1 animate-pulse">
                              <div className="h-3 bg-gray-300 rounded w-full" />
                              <div className="h-3 bg-gray-200 rounded w-2/3" />
                            </div>
                          ) : (
                            <>
                              <span className="text-[13px] font-bold text-gray-900 leading-tight">
                                {value}
                              </span>
                              {label === "Área Propícia" && modeValidProjectionLayer && projectionAreaStats && (
                                <span className="text-[10px] text-gray-400 leading-tight">
                                  {projectionAreaStats.percentage.toLocaleString("pt-BR", {
                                    minimumFractionDigits: 1,
                                    maximumFractionDigits: 1,
                                  })}
                                  % da área total
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {isAreaInadequate && (
                    <Alert
                      variant="warning"
                      icon={<Info size={14} />}
                      className="text-[11px] leading-relaxed"
                    >
                      {getAreaNotAdequateWarning(
                        isSolarEnergyMode ? "solar" : "eólico"
                      )}
                    </Alert>
                  )}

                  {modeValidProjectionLayer && selectedGeometry && (
                    <button
                      onClick={
                        restrictionBreakdown.isOpen
                          ? restrictionBreakdown.close
                          : restrictionBreakdown.open
                      }
                      className="flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-[12px] font-semibold border border-[#0C2856] bg-white text-[#0C2856] cursor-pointer transition-colors hover:bg-gray-50 shrink-0"
                    >
                      {restrictionBreakdown.isOpen
                        ? "Ocultar memorial de cálculo"
                        : "Memorial de cálculo da área propícia"}
                    </button>
                  )}

                  {restrictionBreakdown.isOpen && (
                    <RestrictionBreakdown
                      breakdown={restrictionBreakdown.breakdown}
                      isLoading={restrictionBreakdown.isLoading}
                      error={restrictionBreakdown.error}
                      onHoverRestriction={onHoverRestriction}
                    />
                  )}
                </div>

                <div className="mt-2 flex flex-col gap-2">
                  <button
                    onClick={onCalculateDistance}
                    disabled={!hasSelection || isLoadingDistance}
                    title={!hasSelection ? "Selecione uma área para poder calcular" : undefined}
                    className={[
                      "flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-[12px] font-semibold border-none transition-colors shrink-0 text-center leading-snug",
                      !hasSelection || isLoadingDistance
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-[#0C2856] text-white cursor-pointer hover:bg-[#0a2246]",
                    ].join(" ")}
                  >
                    {isLoadingDistance
                      ? "Calculando…"
                      : "Calcular distância para linha de transmissão mais próxima"}
                  </button>
                  {(distanceData || isLoadingDistance) && (
                    <DistanceInfo
                      distanceData={distanceData ?? {}}
                      drawingName={selectedDrawing?.name}
                      energyMode={isSolarEnergyMode ? "solar" : "eolico"}
                      systemMode={systemMode}
                      isLoading={isLoadingDistance}
                      generationPowerSummary={generationPowerSummary}
                    />
                  )}
                </div>

                {!isAreaInadequate &&
                  (isSolarEnergyMode ? (
                    <div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
                      {/* GHI: exibe apenas o histograma de distribuição sem série mensal */}
                      <>
                        <span className="text-[13px] font-bold text-[#0C2856] tracking-wide text-center">
                          Distribuição de Irradiação (GHI)
                        </span>
                        <DistributionChart
                          distribution={distributionData || []}
                          stats={areaStats || null}
                          unit="kWh/m²/dia"
                          distanceData={distanceData}
                          systemMode={systemMode}
                        />
                      </>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
                      <div className="flex items-center justify-between">
                        <button
                          onClick={prevWindView}
                          aria-label="Ver gráfico eólico anterior"
                          className="bg-transparent border-none cursor-pointer text-gray-300 p-1"
                        >
                          <ChevronLeft size={20} />
                        </button>
                        <span className="text-[13px] font-bold text-[#0C2856] tracking-wide text-center">
                          {getWindViewLabel()}
                        </span>
                        <button
                          onClick={nextWindView}
                          aria-label="Ver próximo gráfico eólico"
                          className="bg-transparent border-none cursor-pointer text-gray-300 p-1"
                        >
                          <ChevronRight size={20} />
                        </button>
                      </div>
                      <div className="min-h-[300px]">{renderWindChart()}</div>
                    </div>
                  ))}
              </div>
            )}
            {mainTab === "legends" && (
              <div className="h-full w-full">
                <LegendTabComponent onClose={onClose} isEmbedded={true} />
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
);
